import { DbDisplayValue, StringUtil } from "@mcleod/core";
import { Checkbox, DialogProps, Label, Panel, PanelProps } from "@mcleod/components";
import { SpecialtyPropertyEditor } from "./SpecialtyPropertyEditor";

export abstract class MultipleValueSelector extends SpecialtyPropertyEditor {
    protected checkboxes: Checkbox[]
    protected values: DbDisplayValue[];

    protected abstract discoverValidValues(): DbDisplayValue[];
    protected abstract getHeadingLabelText(): string;

    constructor(currentValueJson: string, panelProps?: Partial<PanelProps>) {
        super({ fillRow: true, fillHeight: true, minWidth: 500, minHeight: 400, scrollY: true, ...panelProps });
        this.values = this.discoverValidValues();
        const headingLabel = this.createHeadingLabel();
        this.add(headingLabel);
        this.createCheckboxes();
        this.addCheckboxes();
        const selectedValues = this.loadSelectedValues(currentValueJson);
        this.markSelectedValues(selectedValues);
    }

    private loadSelectedValues(currentValueJson: string): string[] {
        if (!StringUtil.isEmptyString(currentValueJson)) {
            return JSON.parse(currentValueJson);
        }
        return [];
    }

    private createHeadingLabel(): Label {
        const providedText = this.getHeadingLabelText();
        const text =
            !StringUtil.isEmptyString(providedText) ? providedText : "Select any combination of the options below:";
        return new Label({ text, fontBold: true });
    }

    protected createCheckboxes() {
        this.checkboxes = this.values.map(dbDisplayValue => {
            const checkbox = new Checkbox({
                caption: dbDisplayValue.displayValue,
                valueChecked: dbDisplayValue.value
            });
            return checkbox;
        });
    }

    protected addCheckboxes() {
        const panelCheckboxes =
            this.checkboxes.length > 5 ? this.layoutCheckboxesTwoColumns() : this.layoutCheckboxesOneColumn();
        this.add(panelCheckboxes);
    }

    private layoutCheckboxesOneColumn(): Panel {
        const panelCheckboxes = this.createPanelCheckboxes();
        panelCheckboxes.add(...this.checkboxes);
        return panelCheckboxes;
    }

    private layoutCheckboxesTwoColumns(): Panel {
        const panelCheckboxes = this.createPanelCheckboxes();
        const panelLeft = Panel.createNoMarginNoPaddingPanel({
            id: "panelCheckboxesLeft", 
            fillRow: true, 
            fillHeight: true, 
            scrollY: false,
            rowBreak: false
        });
        const panelRight = Panel.createNoMarginNoPaddingPanel({
            id: "panelCheckboxesRight", 
            fillRow: true, 
            fillHeight: true, 
            scrollY: false
        });
        panelCheckboxes.add(panelLeft, panelRight);
        const halfway = (this.checkboxes.length / 2) + 1;
        for (let x=0 ; x<this.checkboxes.length ; x++) {
            const targetPanel = x < halfway ? panelLeft : panelRight;
            targetPanel.add(this.checkboxes[x]);
        }
        return panelCheckboxes;
    }

    protected createPanelCheckboxes(): Panel {
        return Panel.createNoMarginNoPaddingPanel({
            id: "panelCheckboxes",
            fillRow: true,
            scrollY: true,
            rowBreak: true
        });
    }

    protected markSelectedValues(selectedValues: string[]) {
        selectedValues.forEach(selectedValue => {
            for (const checkbox of this.checkboxes) {
                if (checkbox.valueChecked === selectedValue) {
                    checkbox.checked = true;
                    break;
                }
            }
        })
    }

    override getDialogProps(): Partial<DialogProps> {
        return {
            ...super.getDialogProps(),
            okDefault: false
        }
    }

    public getResultValue(): string[] {
        const result: string[] = [];
        this.checkboxes.forEach(checkbox => {
            if (checkbox.checked) {
                result.push(checkbox.valueChecked);
            }
        });
        return result;
    }
}
